"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Building2,
  ClipboardCheck,
  ArrowLeftRight,
  Send,
  Users,
  UserPlus,
  ListOrdered,
  Inbox,
  Webhook,
} from "lucide-react";
import CustomerSwitcher from "./CustomerSwitcher";
import IdentitySwitcher from "./IdentitySwitcher";
import OnboardingModal from "./OnboardingModal";

const NAV = [
  { href: "/", label: "Overview", icon: LayoutDashboard },
  { href: "/account", label: "Account", icon: Building2 },
  { href: "/onboarding", label: "Onboarding", icon: ClipboardCheck },
  { href: "/convert", label: "Convert", icon: ArrowLeftRight },
  { href: "/payouts", label: "Payouts", icon: Send },
  { href: "/transactions", label: "Transactions", icon: ListOrdered },
  { href: "/events", label: "Events", icon: Webhook },
  { href: "/permissions", label: "Permissions", icon: Users },
  { href: "/rfis", label: "Compliance Requests", icon: Inbox },
];

export default function Shell({
  children,
  customers,
  activeCustomerId,
  teamMembers = [],
  actingEmail,
  openRfiCount = 0,
  heldDepositCount = 0,
  signaturePendingCount = 0,
  awaitingFundsCount = 0,
}: {
  children: React.ReactNode;
  customers: Array<{ id: string; name: string }>;
  activeCustomerId?: string;
  teamMembers?: Array<{ id: number; name: string | null; email: string; role: string }>;
  actingEmail?: string | null;
  openRfiCount?: number;
  heldDepositCount?: number;
  signaturePendingCount?: number;
  awaitingFundsCount?: number;
}) {
  const pathname = usePathname();
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  return (
    <div className="flex min-h-screen">
      <aside
        className="flex w-[248px] shrink-0 flex-col px-4 py-5"
        style={{ background: "var(--c-charcoal)" }}
      >
        <div className="mb-5 flex flex-col gap-2">
          <CustomerSwitcher customers={customers} activeId={activeCustomerId} />
          <IdentitySwitcher members={teamMembers} actingEmail={actingEmail} />
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-[6px] px-3 py-2 text-left text-[13px] font-medium transition-colors duration-200 hover:bg-[var(--c-green)] hover:text-[var(--c-charcoal)]"
            style={{ color: "var(--c-white)", boxShadow: "inset 0 0 0 1px var(--c-on-dark-hairline)" }}
            onClick={() => setOnboardingOpen(true)}
          >
            <UserPlus size={14} /> New customer
          </button>
        </div>

        <Link href="/" className="mb-8 block px-2">
          <Image
            src="/brand/conduit-lockup-white.svg"
            alt="Conduit"
            width={110}
            height={26}
            priority
          />
        </Link>

        <nav className="flex flex-col gap-1">
          {NAV.map(({ href, label, icon: Icon }) => {
            const active = href === "/" ? pathname === "/" : pathname.startsWith(href);
            // Transactions and Account both surface blocked money (held
            // deposits, signature-pending withdrawals, unfunded conversions) —
            // badge both so the dot is visible wherever the user happens to
            // be looking. Convert gets a dot for unfunded orders too, since
            // that's where they can be tracked or cancelled.
            const blockedCount = heldDepositCount + signaturePendingCount + awaitingFundsCount;
            const badgeCount =
              href === "/rfis"
                ? openRfiCount
                : href === "/transactions" || href === "/account"
                  ? blockedCount
                  : href === "/convert"
                    ? awaitingFundsCount
                    : 0;
            const showBadge = badgeCount > 0;
            const badgeTitle =
              href === "/rfis"
                ? `${badgeCount} unanswered compliance request${badgeCount === 1 ? "" : "s"}`
                : [
                    heldDepositCount > 0
                      ? `${heldDepositCount} deposit${heldDepositCount === 1 ? "" : "s"} held awaiting sender information`
                      : null,
                    signaturePendingCount > 0
                      ? `${signaturePendingCount} transaction${signaturePendingCount === 1 ? "" : "s"} blocked on your signature`
                      : null,
                    awaitingFundsCount > 0
                      ? `${awaitingFundsCount} conversion${awaitingFundsCount === 1 ? "" : "s"} waiting for funding`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
            return (
              <Link
                key={href}
                href={href}
                className="relative flex items-center gap-3 rounded-[6px] px-3 py-2 text-[14px] font-medium transition-colors duration-200"
                style={
                  active
                    ? { background: "var(--c-green)", color: "var(--c-charcoal)" }
                    : { color: "var(--c-on-dark-soft)" }
                }
              >
                <span className="relative shrink-0">
                  <Icon size={16} strokeWidth={2} />
                  {showBadge && (
                    <span
                      className="absolute -right-[3px] -top-[3px] block h-[7px] w-[7px] rounded-full"
                      style={{ background: "var(--c-danger)", boxShadow: "0 0 0 1.5px var(--c-charcoal)" }}
                      title={badgeTitle}
                    />
                  )}
                </span>
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto px-3">
          <div
            className="eyebrow border-t pt-4"
            style={{ color: "var(--c-on-dark-soft)", borderColor: "var(--c-on-dark-hairline)" }}
          >
            anymoney. anytime.
            <br />
            anywhere.
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 px-10 py-8">{children}</main>

      {onboardingOpen && <OnboardingModal onClose={() => setOnboardingOpen(false)} />}
    </div>
  );
}
